/**
 * Full Width Collection Grid JavaScript
 * Integrated with Nexus Motion System
 */

class FullWidthCollectionGrid {
  constructor(sectionId) {
    this.section = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (!this.section) return;

    this.tiles = this.section.querySelectorAll('.collection-tile');
    this.panels = this.section.querySelectorAll('.products-panel');
    this.isOpen = false;
    this.currentPanel = null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.setupIntersectionObserver();
    this.initializeMotionSystem();
  }

  bindEvents() {
    // Explore button click events
    this.section.addEventListener('click', (e) => {
      const exploreBtn = e.target.closest('[data-action="open-products"]');
      const closeBtn = e.target.closest('[data-action="close-panel"]');

      if (exploreBtn) {
        e.preventDefault();
        const tile = exploreBtn.closest('.collection-tile');
        this.openProductsPanel(tile);
      }

      if (closeBtn) {
        e.preventDefault();
        this.closeProductsPanel();
      }
    });

    // Close panel when clicking outside
    document.addEventListener('click', (e) => {
      if (this.isOpen && !e.target.closest('.products-panel') && !e.target.closest('.explore-button')) {
        this.closeProductsPanel();
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeProductsPanel();
      }
    });

    // Horizontal scroll support
    this.grid = this.section.querySelector('.fullwidth-collection-grid');
    if (this.grid) {
      this.initDragToScroll();
      this.initChevronNav();
    }
  }

  initDragToScroll() {
    let isDown = false;
    let startX;
    let scrollLeft;

    this.grid.addEventListener('mousedown', (e) => {
      isDown = true;
      this.grid.classList.add('dragging');
      startX = e.pageX - this.grid.offsetLeft;
      scrollLeft = this.grid.scrollLeft;
      this.grid.style.scrollSnapType = 'none'; // Disable snap during drag
    });

    this.grid.addEventListener('mouseleave', () => {
      isDown = false;
      this.grid.classList.remove('dragging');
    });

    this.grid.addEventListener('mouseup', () => {
      isDown = false;
      this.grid.classList.remove('dragging');
      this.grid.style.scrollSnapType = 'x mandatory'; // Restore snap
    });

    this.grid.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - this.grid.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed
      this.grid.scrollLeft = scrollLeft - walk;
    });
  }

  initChevronNav() {
    const prevBtn = this.section.querySelector('[data-action="prev-slide"]');
    const nextBtn = this.section.querySelector('[data-action="next-slide"]');

    if (prevBtn && nextBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const scrollAmount = this.grid.clientWidth * 0.8;
        this.grid.scrollTo({
          left: this.grid.scrollLeft - scrollAmount,
          behavior: 'smooth'
        });
      });

      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const scrollAmount = this.grid.clientWidth * 0.8;
        this.grid.scrollTo({
          left: this.grid.scrollLeft + scrollAmount,
          behavior: 'smooth'
        });
      });
    }
  }

  openProductsPanel(tile) {
    // If another panel is open, close it first
    if (this.isOpen && this.currentPanel) {
      this.closeProductsPanel();
      // Small delay to ensure smooth transition
      setTimeout(() => {
        this._openNewPanel(tile);
      }, 300);
    } else {
      // No panel open, open directly
      this._openNewPanel(tile);
    }
  }

  _openNewPanel(tile) {
    const panel = tile.querySelector('.products-panel');
    if (!panel) return;

    this.currentPanel = panel;
    this.isOpen = true;

    // Allow page scrolling - removed scroll locking
    // Change plus icon to minus in the explore button
    const exploreBtn = tile.querySelector('.explore-button');
    if (exploreBtn) {
      const plusIcon = exploreBtn.querySelector('.plus-icon');
      if (plusIcon) {
        plusIcon.textContent = '−'; // Minus sign
      }
    }

    // Add active class with optimized animation
    requestAnimationFrame(() => {
      panel.classList.add('active');

      // Add motion classes to products with faster timing
      const products = panel.querySelectorAll('.product-item');
      products.forEach((product, index) => {
        setTimeout(() => {
          product.classList.add('visible');
        }, index * 30); // Faster animation
      });
    });

    // Remove dimming effect on other tiles
    this.tiles.forEach(otherTile => {
      if (otherTile !== tile) {
        otherTile.style.opacity = '';
        otherTile.style.transform = '';
      }
    });

    // Overlay removed for better performance

    // Trigger custom event
    this.dispatchCustomEvent('panelOpened', { tile, panel });
  }

  closeProductsPanel() {
    if (!this.isOpen || !this.currentPanel) return;

    // Remove active class
    this.currentPanel.classList.remove('active');

    // Reset other tiles
    this.tiles.forEach(tile => {
      tile.style.opacity = '';
      tile.style.transform = '';
      tile.style.filter = '';
    });

    // Restore plus icon in explore button
    this._restorePlusIcon();

    // Page scrolling was never locked, so no need to restore

    this.isOpen = false;
    this.currentPanel = null;

    // Trigger custom event
    this.dispatchCustomEvent('panelClosed');
  }

  _restorePlusIcon() {
    if (this.currentPanel) {
      const tile = this.currentPanel.closest('.collection-tile');
      if (tile) {
        const exploreBtn = tile.querySelector('.explore-button');
        if (exploreBtn) {
          const plusIcon = exploreBtn.querySelector('.plus-icon');
          if (plusIcon) {
            plusIcon.textContent = '+'; // Plus sign
          }
        }
      }
    }
  }

  // Overlay functions removed for better performance and to prevent sticking issues

  setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      // Observe section for fade-in
      observer.observe(this.section);

      // Observe individual tiles for staggered animation
      this.tiles.forEach(tile => {
        observer.observe(tile);
      });
    } else {
      // Fallback for older browsers
      this.section.classList.add('visible');
      this.tiles.forEach(tile => tile.classList.add('visible'));
    }
  }

  initializeMotionSystem() {
    // Integrate with existing motion system if available
    if (window.NexusMotionEngine) {
      window.NexusMotionEngine.registerElement(this.section, {
        animation: 'fadeInUp',
        duration: 1200,
        delay: 200,
        easing: 'cubic-bezier(0.23, 1, 0.32, 1)'
      });

      this.tiles.forEach((tile, index) => {
        window.NexusMotionEngine.registerElement(tile, {
          animation: 'slideInUp',
          duration: 1000,
          delay: 200 + (index * 200),
          easing: 'cubic-bezier(0.23, 1, 0.32, 1)'
        });
      });
    }

    // Custom scroll-based animations
    this.setupScrollAnimations();
  }

  setupScrollAnimations() {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateOnScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  updateOnScroll() {
    // Scroll-based parallax and opacity removed to prevent vertical movement and flickering
    // Section remains stable as per user request
    return;
  }

  dispatchCustomEvent(eventName, detail = {}) {
    const event = new CustomEvent(`fullwidthCollection:${eventName}`, {
      detail: {
        section: this.section,
        ...detail
      },
      bubbles: true
    });
    this.section.dispatchEvent(event);
  }

  // Public methods for external control
  destroy() {
    // Clean up event listeners and observers
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    document.body.style.overflow = '';
  }

  refresh() {
    // Re-initialize after DOM changes
    this.tiles = this.section.querySelectorAll('.collection-tile');
    this.panels = this.section.querySelectorAll('.products-panel');
    this.bindEvents();
    this.setupIntersectionObserver();
  }
}

// Auto-initialization
document.addEventListener('DOMContentLoaded', () => {
  // Initialize all full width collection grids
  const sections = document.querySelectorAll('[data-section-id]');

  sections.forEach(section => {
    const sectionId = section.getAttribute('data-section-id');
    if (section.closest('.section-fullwidth-collection')) {
      new FullWidthCollectionGrid(sectionId);
    }
  });
});

// Shopify section events (if in Shopify environment)
if (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.sections) {
  Shopify.theme.sections.register('fullwidth-collection-grid', FullWidthCollectionGrid);
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FullWidthCollectionGrid;
}